/**
 * Grouped query images: a COVERAGE_REVIEW task can bundle several photos of
 * the same planogram, a primary carrying the CV detections plus similar views.
 * Every submitted box names its image with `image_id`; absent means primary.
 *
 * Spec: lebron `agent_docs/specs/pilot_pod_grouped_images_mechanism.md` §B.
 */
import type { OoSParcelSchema } from "@augmodo/lebron-browser-client";

export interface GroupImage {
  imageId: string;
  role: string;
  isPrimary: boolean;
  /** 1-indexed position in the strip. */
  viewNumber: number;
  capturedAtUtc?: string;
  /** Seconds between this view's capture and the primary's. */
  deltaTs?: number;
  /** Fraction of the primary's shelf area this view also shows. */
  overlapFrac?: number;
  viewOrthogonality?: number;
}

const numberAt = (
  metadata: Record<string, unknown> | undefined,
  key: string,
  index: number,
): number | undefined => {
  const arr = metadata?.[key];
  if (!Array.isArray(arr)) return undefined;
  const value = arr[index];
  return typeof value === "number" ? value : undefined;
};

const stringAt = (
  metadata: Record<string, unknown> | undefined,
  key: string,
  index: number,
): string | undefined => {
  const arr = metadata?.[key];
  if (!Array.isArray(arr)) return undefined;
  const value = arr[index];
  return typeof value === "string" ? value : undefined;
};

/** Annotatable images, primary first. Empty for an ungrouped parcel. */
export const getGroupImages = (
  parcel: OoSParcelSchema | undefined,
  primaryImageId: string | undefined,
): GroupImage[] => {
  const metadata = parcel?.metadata as Record<string, unknown> | undefined;
  const imageIds = metadata?.image_ids;
  if (!Array.isArray(imageIds) || imageIds.length <= 1) return [];

  const images: GroupImage[] = [];
  imageIds.forEach((id, i) => {
    if (typeof id !== "string") return;
    const role = stringAt(metadata, "group_role", i) ?? "similar_view";
    images.push({
      imageId: id,
      role,
      // Every index in the result is anchored to the task's own image.
      isPrimary: primaryImageId ? id === primaryImageId : role === "primary",
      viewNumber: 0,
      capturedAtUtc: stringAt(metadata, "captured_at_utc", i),
      deltaTs: numberAt(metadata, "delta_t_s", i),
      overlapFrac: numberAt(metadata, "overlap_frac", i),
      viewOrthogonality: numberAt(metadata, "view_orthogonality", i),
    });
  });

  const ordered = [
    ...images.filter((image) => image.isPrimary),
    ...images.filter((image) => !image.isPrimary),
  ];
  return ordered.map((image, i) => ({ ...image, viewNumber: i + 1 }));
};

export const boxImageId = (
  box: { imageId?: string | null },
  primaryImageId: string,
): string => box.imageId ?? primaryImageId;

/** Appends `image_id` last — the golden fixtures pin key order. */
export const withImageId = <T extends object>(
  box: T,
  imageId: string,
): T & { image_id: string } => ({ ...box, image_id: imageId });

export const hiddenIndicesForImage = (
  alreadyHidden: Set<number>,
  boxImageIds: (string | null | undefined)[],
  primaryImageId: string,
  activeImageId: string,
): Set<number> => {
  const hidden = new Set(alreadyHidden);
  boxImageIds.forEach((imageId, i) => {
    if ((imageId ?? primaryImageId) !== activeImageId) hidden.add(i);
  });
  return hidden;
};
